"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { PageHeader } from "@/components/ui/PageHeader";

interface Preset {
  key: string;
  category: string;
  label: string;
  width: number;
  height: number;
}
interface Quota {
  tier: string;
  monthly_quota: number;
  used: number;
  remaining: number;
  bonus_remaining: number;  // R3-33 S3 — 가입 보너스 잔여 (만료 X)
  resets_at: string;
}
interface GenerateOut {
  request_id: number;
  status: string;
  output_url: string | null;
  width: number | null;
  height: number | null;
  elapsed_sec: number | null;
  cost_krw: number;
  used_after: number;
  remaining_after: number;
}
interface HistoryRow {
  id: number;
  preset_key: string | null;
  custom_prompt: string | null;
  status: string;
  output_url: string | null;
  width: number | null;
  height: number | null;
  is_free_regen: boolean;
  created_at: string;
}

const CATEGORY_LABEL: Record<string, string> = {
  sns: "📱 SNS",
  delivery: "🍴 배달앱",
  external: "🌐 외부 채널",
};

export default function ImageStudioPage() {
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // 2026-05-12 — 사용자 정의 사이즈 (프리셋 선택 시 자동 채움 + 수정 가능)
  const [customWidth, setCustomWidth] = useState<string>("");
  const [customHeight, setCustomHeight] = useState<string>("");
  const [presets, setPresets] = useState<Preset[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("sns");
  const [pickedPreset, setPickedPreset] = useState<string | null>(null);
  // 2026-05-12 — 디테일 조정: 게이지 4개 (조명/색온도/채도/콘트라스트) + 텍스트 2개 (배경/추가)
  // 2026-06-02 — 0~100 step=1 (마우스 부드러움). prompt 매핑은 25 단위로 5 구간 분할.
  const [lightingLevel, setLightingLevel] = useState<number>(50);
  const [warmthLevel, setWarmthLevel] = useState<number>(50);
  const [saturationLevel, setSaturationLevel] = useState<number>(50);
  const [contrastLevel, setContrastLevel] = useState<number>(50);
  const [background, setBackground] = useState<string>("");
  const [extra, setExtra] = useState<string>("");

  // 0~100 → 0~4 인덱스 변환 (prompt 매핑용)
  const toBucket = (v: number) => Math.min(4, Math.max(0, Math.round(v / 25)));

  // 게이지 값 → prompt 변환 (기본 50 일 때 = bucket 2 = 추가 X)
  const customPrompt = (() => {
    const parts: string[] = [];
    const lightingMap = ["very dim moody dark lighting", "soft low ambient light", "", "bright daylight feel", "strong studio bright lighting"];
    const warmthMap = ["cool blue tone", "slightly cool tone", "", "slightly warm tone", "warm golden hour tone"];
    const satMap = ["desaturated vintage feel", "muted colors", "", "vivid colors", "highly saturated bold colors"];
    const contrastMap = ["soft hazy low contrast", "low contrast soft", "", "high contrast crisp", "dramatic sharp contrast"];
    const lb = toBucket(lightingLevel), wb = toBucket(warmthLevel), sb = toBucket(saturationLevel), cb = toBucket(contrastLevel);
    if (lightingMap[lb]) parts.push(`Lighting: ${lightingMap[lb]}`);
    if (warmthMap[wb]) parts.push(`Color temp: ${warmthMap[wb]}`);
    if (satMap[sb]) parts.push(`Saturation: ${satMap[sb]}`);
    if (contrastMap[cb]) parts.push(`Contrast: ${contrastMap[cb]}`);
    if (background.trim()) parts.push(`Background/Props: ${background.trim()}`);
    if (extra.trim()) parts.push(extra.trim());
    return parts.join(" / ");
  })();

  // 게이지 → 사용자 표시 라벨 (한국어)
  const SLIDER_LABELS = {
    lighting: ["어두운 무드", "약간 어두운", "자연 조명", "밝은 낮", "강한 스튜디오"],
    warmth: ["차가운 푸르름", "약간 차가운", "중성", "약간 따뜻", "노을 따뜻"],
    saturation: ["탁한 빈티지", "약간 탁한", "자연 색감", "선명한", "비비드 진한"],
    contrast: ["부드러운 뽀얀", "약간 부드러운", "자연 또렷함", "또렷한", "드라마틱"],
  };
  const [quota, setQuota] = useState<Quota | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GenerateOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);

  const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;

  // R3-33 (2026-04-28) — 백엔드가 signed URL (HMAC) 반환. ?t=jwt 추가 X.
  const withToken = (url: string | null): string => url || "";

  // 프리셋 카탈로그
  useEffect(() => {
    axios.get<{ items: Preset[] }>("/api/image-studio/presets")
      .then((r) => setPresets(r.data.items))
      .catch(() => setPresets([]));
  }, []);

  // quota + history (로그인 시)
  const reloadQuota = () => {
    if (!token) return;
    axios.get<Quota>("/api/image-studio/quota", { headers: authHeader })
      .then((r) => setQuota(r.data))
      .catch(() => setQuota(null));
  };
  const reloadHistory = () => {
    if (!token) return;
    axios.get<{ items: HistoryRow[] }>("/api/image-studio/history", { headers: authHeader })
      .then((r) => setHistory(r.data.items))
      .catch(() => setHistory([]));
  };
  useEffect(() => {
    reloadQuota();
    reloadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError("최대 10MB 까지 업로드 가능");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    setError(null);
  };

  const grouped = presets.reduce<Record<string, Preset[]>>((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  const submit = async () => {
    if (!token) {
      setError("로그인이 필요합니다");
      return;
    }
    if (!file) {
      setError("사진을 선택하세요");
      return;
    }
    if (!pickedPreset && !customPrompt.trim()) {
      setError("프리셋 선택 또는 자유 컨셉 입력 필요");
      return;
    }
    if (quota && quota.remaining <= 0 && (quota.bonus_remaining ?? 0) <= 0) {
      setError(
        quota.monthly_quota > 0
          ? `이번 달 한도 ${quota.monthly_quota}매 모두 사용. 다음 달 1일 리셋`
          : `${quota.tier} 티어는 이미지 편집 미지원 — 유료 구독 필요`,
      );
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("image", file);
      if (pickedPreset) fd.append("preset_key", pickedPreset);
      if (customPrompt.trim()) fd.append("custom_prompt", customPrompt.trim());
      // 2026-05-12 — 사용자 정의 사이즈 (프리셋 사이즈 override)
      const w = parseInt(customWidth, 10);
      const h = parseInt(customHeight, 10);
      if (w >= 256 && w <= 10000 && h >= 256 && h <= 10000) {
        fd.append("custom_width", String(w));
        fd.append("custom_height", String(h));
      }
      const r = await axios.post<GenerateOut>("/api/image-studio/generate", fd, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
        timeout: 60_000,
      });
      setResult(r.data);
      reloadQuota();
      reloadHistory();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? "생성 실패: " + (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!result || !token) return;
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post<GenerateOut>(
        `/api/image-studio/regenerate?request_id=${result.request_id}`,
        {},
        { headers: authHeader, timeout: 60_000 },
      );
      setResult(r.data);
      reloadQuota();
      reloadHistory();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? "재생성 실패: " + (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const usagePercent = quota && quota.monthly_quota > 0
    ? Math.round((quota.used / quota.monthly_quota) * 100)
    : 0;
  const usageWarning = usagePercent >= 80;

  // β-8 v3 — hydration 대기
  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2 text-text-sub text-sm">
          <span className="w-4 h-4 border-2 border-text-sub/30 border-t-primary rounded-full animate-spin" />
          <span>로딩 중...</span>
        </div>
      </div>
    );
  }
  if (!isLoggedIn) {
    return (
      <div className="px-4 py-8 max-w-lg mx-auto">
        <h1 className="text-xl font-black text-text mb-3">🎨 AI 이미지 스튜디오</h1>
        <div className="card text-center py-10 space-y-3">
          <p className="text-3xl">🔒</p>
          <p className="text-sm text-text">로그인이 필요한 기능이에요</p>
          <Link href="/login" className="inline-block btn-primary text-sm px-5 py-2">
            로그인
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <PageHeader title="🎨 AI 이미지 스튜디오" backHref="/mypage" />
      <div className="px-4 py-5 max-w-2xl mx-auto">
      <header className="mb-4">
        <p className="text-xs text-text-sub mt-1">
          매장 메뉴 사진 업로드 → 프리셋 또는 자유 컨셉으로 변환. SNS·메뉴판·배달앱 즉시 사용 가능
        </p>
      </header>

      {/* Quota */}
      {quota && (
        <div className={`card mb-4 ${usageWarning ? "border-yellow-400/40" : ""}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-2xs text-text-sub">이번 달 사용</p>
              <p className="text-lg font-bold text-text">
                {quota.used} / {quota.monthly_quota}
                <span className="text-xs font-normal text-text-sub ml-1">매 ({quota.tier})</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(quota.bonus_remaining ?? 0) > 0 && (
                <span className="text-2xs bg-primary/20 text-primary px-2 py-1 rounded font-bold">
                  🎁 가입 보너스 {quota.bonus_remaining}매
                </span>
              )}
              {usageWarning && (
                <p className="text-2xs text-yellow-400 text-right">⚠ 80%</p>
              )}
            </div>
          </div>
          <div className="w-full bg-bg-elevated rounded h-1.5 mt-2 overflow-hidden">
            <div
              className={`h-full transition-all ${usageWarning ? "bg-yellow-400" : "bg-primary"}`}
              style={{ width: `${Math.min(100, usagePercent)}%` }}
            />
          </div>
          {quota.monthly_quota === 0 && (quota.bonus_remaining ?? 0) > 0 && (
            <p className="text-2xs text-primary mt-2">
              🎁 가입 보너스 {quota.bonus_remaining}매로 무료 체험 가능. 이후 유료 구독 필요
            </p>
          )}
          {quota.monthly_quota === 0 && (quota.bonus_remaining ?? 0) === 0 && (
            <p className="text-2xs text-yellow-400 mt-2">
              ⓘ 현재 티어({quota.tier})는 미지원. 유료 구독 시 월 30매 사용 가능
            </p>
          )}
        </div>
      )}

      {/* 1. 사진 업로드 */}
      <section className="card mb-4">
        <h2 className="text-sm font-bold text-text mb-2">1. 사진 업로드</h2>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onFileChange}
          className="hidden"
        />
        {previewUrl ? (
          <div className="relative">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={previewUrl} alt="preview" className="w-full max-h-64 object-contain rounded bg-bg-elevated" />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 text-xs text-text-sub underline"
            >
              다른 사진 선택
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-8 border-2 border-dashed border-border rounded text-text-sub hover:text-text hover:border-primary/40"
          >
            📷 사진 선택 (JPG/PNG, 최대 10MB)
          </button>
        )}
      </section>

      {/* 2. 컨셉 선택 — 카테고리 탭 + 프리셋 */}
      <section className="card mb-4">
        <h2 className="text-sm font-bold text-text mb-2">2. 컨셉 선택</h2>

        {/* 카테고리 탭 */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-3 hide-scrollbar">
          {Object.keys(CATEGORY_LABEL).map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
                activeCategory === cat
                  ? "bg-primary text-bg"
                  : "bg-bg-elevated text-text-sub"
              }`}
            >
              {CATEGORY_LABEL[cat]}
            </button>
          ))}
        </div>

        {/* 프리셋 그리드 */}
        <div className="grid grid-cols-2 gap-2">
          {(grouped[activeCategory] || []).map((p) => (
            <button
              key={p.key}
              onClick={() => {
                const next = p.key === pickedPreset ? null : p.key;
                setPickedPreset(next);
                // 2026-05-12 — 프리셋 선택 시 사이즈 자동 채움 (사용자 수정 가능)
                if (next) {
                  setCustomWidth(String(p.width));
                  setCustomHeight(String(p.height));
                } else {
                  setCustomWidth("");
                  setCustomHeight("");
                }
              }}
              className={`text-left p-2.5 rounded-md border transition-colors ${
                pickedPreset === p.key
                  ? "bg-primary/10 border-primary"
                  : "bg-bg-elevated border-border"
              }`}
            >
              <p className="text-xs font-bold text-text">{p.label}</p>
              <p className="text-2xs text-text-sub mt-0.5">{p.width}×{p.height}</p>
            </button>
          ))}
        </div>

        {/* 2026-05-12 — 사이즈 직접 수정 (선택, 프리셋 사이즈 자동 채워짐) */}
        {(pickedPreset || customWidth || customHeight) && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-2xs text-text-sub mb-2">✏ 사이즈 직접 수정 (선택)</p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={customWidth}
                onChange={(e) => setCustomWidth(e.target.value)}
                placeholder="가로"
                min={256}
                max={10000}
                className="flex-1 px-3 py-2 text-sm bg-bg-elevated border border-border rounded text-text"
              />
              <span className="text-text-sub">×</span>
              <input
                type="number"
                value={customHeight}
                onChange={(e) => setCustomHeight(e.target.value)}
                placeholder="세로"
                min={256}
                max={10000}
                className="flex-1 px-3 py-2 text-sm bg-bg-elevated border border-border rounded text-text"
              />
              <span className="text-2xs text-text-sub">px</span>
            </div>
            <p className="text-2xs text-text-sub mt-1">
              ⓘ 256~10000px. 프리셋 사이즈 그대로 두거나 원하는 값으로 변경
            </p>
          </div>
        )}

        {pickedPreset && (
          <p className="text-2xs text-primary mt-2">
            ✓ 선택: {presets.find((p) => p.key === pickedPreset)?.label}
            <button
              onClick={() => setPickedPreset(null)}
              className="ml-2 underline text-text-sub"
            >
              해제
            </button>
          </p>
        )}
      </section>

      {/* 3. 디테일 조정 (선택, 2026-05-12 — 1 미리보기 + 4 슬라이더 통합) */}
      <section className="card mb-4 space-y-3">
        <h2 className="text-sm font-bold text-text">
          3. 디테일 조정 <span className="text-2xs text-text-sub font-normal">(선택, 안 만지면 자연 그대로)</span>
        </h2>

        {/* 1 큰 미리보기 — 사용자 업로드 사진 기반 CSS filter 실시간 변환 */}
        {previewUrl ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={previewUrl}
              alt="디테일 조정 미리보기"
              className="w-full h-48 object-contain rounded bg-bg-elevated"
              style={{
                // 2026-06-02 — 0~100 선형 보간 (50 = 중성). 마우스 부드럽게.
                filter: [
                  `brightness(${(0.5 + (lightingLevel / 100) * 1.0).toFixed(2)})`,        // 0.5 ~ 1.5
                  `saturate(${(0.3 + (saturationLevel / 100) * 1.5).toFixed(2)})`,        // 0.3 ~ 1.8
                  `contrast(${(0.75 + (contrastLevel / 100) * 0.65).toFixed(2)})`,        // 0.75 ~ 1.4
                  warmthLevel < 50
                    ? `hue-rotate(${((50 - warmthLevel) / 50 * -25).toFixed(0)}deg)`
                    : warmthLevel > 50
                    ? `sepia(${((warmthLevel - 50) / 50 * 0.35).toFixed(2)})`
                    : "",
                ].filter(Boolean).join(" "),
              }}
            />
            <p className="text-2xs text-text-sub text-center">
              🔍 슬라이더 움직이면 위 미리보기가 실시간 변환됩니다
            </p>
          </>
        ) : (
          <div className="w-full h-32 rounded bg-bg-elevated flex items-center justify-center">
            <p className="text-xs text-text-sub text-center">
              💡 위에서 사진 업로드 후 슬라이더 효과 미리보기 가능
            </p>
          </div>
        )}

        {/* 4 슬라이더 묶음 */}
        <SliderRow
          icon="☀"
          label="조명 / 채광"
          value={lightingLevel}
          onChange={setLightingLevel}
          labels={SLIDER_LABELS.lighting}
        />
        <SliderRow
          icon="🌡"
          label="색온도"
          value={warmthLevel}
          onChange={setWarmthLevel}
          labels={SLIDER_LABELS.warmth}
        />
        <SliderRow
          icon="🎨"
          label="채도"
          value={saturationLevel}
          onChange={setSaturationLevel}
          labels={SLIDER_LABELS.saturation}
        />
        <SliderRow
          icon="🌫"
          label="콘트라스트"
          value={contrastLevel}
          onChange={setContrastLevel}
          labels={SLIDER_LABELS.contrast}
        />

        {/* 텍스트 2 */}
        <div>
          <label className="text-2xs text-text-sub mb-1 block">
            🖼 배경 / 소품
          </label>
          {/* 2026-05-12 — 추천 칩 (multi-select, 클릭 = 자유 입력란에 자동 추가) */}
          <BackgroundChips
            current={background}
            onAdd={(text) => {
              const items = background.split(",").map((s) => s.trim()).filter(Boolean);
              if (items.includes(text)) {
                // 같은 칩 다시 클릭 = 제거
                setBackground(items.filter((s) => s !== text).join(", "));
              } else {
                setBackground([...items, text].join(", "));
              }
            }}
          />
          <input
            type="text"
            value={background}
            onChange={(e) => setBackground(e.target.value)}
            placeholder="칩 클릭 또는 직접 입력 (예: 원목 테이블, 대리석, 화분 곁들임)"
            className="w-full px-3 py-2 text-sm bg-bg-elevated border border-border rounded text-text mt-2"
            maxLength={200}
          />
        </div>
        <div>
          <label className="text-2xs text-text-sub mb-1 block">
            💡 추가 요청
          </label>
          <input
            type="text"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="예: 김 모락모락, 손이 들고 있는 느낌, 한국어 텍스트 자리 위쪽"
            className="w-full px-3 py-2 text-sm bg-bg-elevated border border-border rounded text-text"
            maxLength={200}
          />
        </div>
        <p className="text-2xs text-text-sub">
          ⓘ 슬라이더 자연 (가운데) 일 때 = 영향 X. 양 끝으로 갈수록 효과 강해짐
        </p>
      </section>

      {/* 4. 생성 */}
      <section className="card mb-4">
        {error && (
          <p className="text-xs text-red-400 mb-2">{error}</p>
        )}
        <button
          onClick={submit}
          disabled={
            busy
            || !file
            || (!pickedPreset && !customPrompt.trim())
            || ((quota?.remaining ?? 0) <= 0 && (quota?.bonus_remaining ?? 0) <= 0)
          }
          className="w-full btn-primary text-sm py-3 disabled:opacity-30"
        >
          {busy
            ? "🎨 AI 이미지 생성 중... (~15초)"
            : (quota?.remaining ?? 0) <= 0 && (quota?.bonus_remaining ?? 0) > 0
              ? `🎁 가입 보너스로 생성 (잔여 ${quota?.bonus_remaining}매)`
              : "✨ AI 이미지 생성"}
        </button>
        {busy && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-2xs text-text-sub">AI 가 그리는 중 — 평균 15초</span>
          </div>
        )}
      </section>

      {/* 결과 */}
      {result && result.output_url && (
        <section className="card mb-4 border-primary/40">
          <h2 className="text-sm font-bold text-text mb-2">🎉 결과</h2>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={withToken(result.output_url)}
            alt="result"
            className="w-full rounded bg-bg-elevated"
          />
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              onClick={async () => {
                // 2026-05-12 fix — 모바일 iOS Safari 가 <a download> 무시.
                // blob fetch → URL.createObjectURL 패턴 으로 모바일/PC 일관 다운로드.
                if (!result?.output_url) return;
                try {
                  const resp = await fetch(withToken(result.output_url));
                  if (!resp.ok) throw new Error("fetch fail");
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `trendrain_${result.request_id}.png`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch {
                  setError("다운로드 실패 — 이미지 길게 누르기 → 사진 저장 으로 받아주세요");
                }
              }}
              className="text-center text-xs font-semibold py-2.5 border border-border rounded text-text"
            >
              📥 다운로드
            </button>
            <button
              onClick={regenerate}
              disabled={busy}
              className="text-xs font-semibold py-2.5 bg-primary/10 border border-primary/40 text-primary rounded disabled:opacity-30"
            >
              🔄 재생성 (1회 무료)
            </button>
          </div>
          {/* 2026-05-12 — SNS 글작성 연계 */}
          <a
            href={`/sns-writer?image_ctx=${encodeURIComponent(
              [pickedPreset && `프리셋: ${pickedPreset}`, customPrompt].filter(Boolean).join(" / ").slice(0, 200)
            )}`}
            className="mt-2 block text-center text-xs font-bold py-2.5 bg-primary text-bg rounded"
          >
            📝 이 이미지로 SNS 글 만들기 →
          </a>
          <p className="text-2xs text-text-sub mt-2 leading-relaxed">
            ※ {result.width && result.height && `${result.width}×${result.height}px · `}
            소요 {result.elapsed_sec?.toFixed(1)}초 · 비용 {result.cost_krw}원<br />
            ※ 결과 마음에 안 들면 재생성 1회 무료. 그 다음부터 quota 차감
          </p>
        </section>
      )}

      {/* 히스토리 */}
      {history.length > 0 && (
        <section className="card">
          <h2 className="text-sm font-bold text-text mb-2">📜 최근 생성</h2>
          <div className="grid grid-cols-3 gap-2">
            {history.slice(0, 9).map((h) => (
              <div key={h.id} className="aspect-square bg-bg-elevated rounded overflow-hidden relative">
                {h.output_url ? (
                  <a href={withToken(h.output_url)} target="_blank" rel="noreferrer">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={withToken(h.output_url)} alt={String(h.id)} className="w-full h-full object-cover" />
                  </a>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xs text-text-sub">
                    {h.status}
                  </div>
                )}
                {h.is_free_regen && (
                  <span className="absolute top-1 right-1 text-2xs bg-primary/80 text-bg px-1 rounded">재</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      </div>
    </div>
  );
}


// 2026-05-12 — 배경/소품 추천 칩 (multi-select)
// 3 카테고리 (배경 / 자연 / 카페 소품) — 사용자 막막함 해결
const BG_CHIP_GROUPS: { label: string; emoji: string; chips: string[] }[] = [
  {
    label: "배경",
    emoji: "🪵",
    chips: ["원목 테이블", "대리석", "흰 도자기 접시", "크림 리넨", "검은 슬레이트", "파스텔 분홍 종이", "베이지 천", "흰 종이"],
  },
  {
    label: "자연 소품",
    emoji: "🌿",
    chips: ["단풍잎", "민트 잎", "꽃잎", "허브", "솔잎", "마른 꽃", "건자두"],
  },
  {
    label: "카페 소품",
    emoji: "☕",
    chips: ["커피 콩", "시나몬 스틱", "설탕 파우더", "초콜릿 조각", "원두 가루", "라떼아트 컵"],
  },
];

function BackgroundChips({
  current,
  onAdd,
}: {
  current: string;
  onAdd: (text: string) => void;
}) {
  const items = current.split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-1.5 mb-1">
      {BG_CHIP_GROUPS.map((g) => (
        <div key={g.label}>
          <p className="text-[10px] text-text-sub mb-1">
            {g.emoji} {g.label}
          </p>
          <div className="flex flex-wrap gap-1">
            {g.chips.map((c) => {
              const active = items.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => onAdd(c)}
                  className={`text-2xs px-2 py-1 rounded-full border transition-colors ${
                    active
                      ? "border-primary bg-primary/10 text-primary font-bold"
                      : "border-border text-text-sub hover:text-text hover:border-text-sub"
                  }`}
                >
                  {active ? "✓ " : "+ "}{c}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// 2026-05-12 — 1 미리보기 + 4 슬라이더 통합 후 SliderRow 는 단순 range input 만
function SliderRow({
  icon,
  label,
  value,
  onChange,
  labels,
}: {
  icon: string;
  label: string;
  value: number;       // 2026-06-02 — 0~100 (1 단위, 마우스 부드러움)
  onChange: (v: number) => void;
  labels: string[];    // 5 라벨 (0/25/50/75/100 매핑)
}) {
  // 가장 가까운 라벨 (현재 값 → 0~4 인덱스)
  const bucket = Math.min(4, Math.max(0, Math.round(value / 25)));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-2xs text-text-sub">
          {icon} {label}
        </label>
        <span className="text-2xs font-semibold text-text">
          {labels[bucket]}
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-primary"
      />
      <div className="flex justify-between text-[9px] text-text-sub mt-0.5 px-0.5">
        <span>{labels[0]}</span>
        <span className="opacity-60">{labels[2]}</span>
        <span>{labels[4]}</span>
      </div>
    </div>
  );
}
